import { useEffect, useState } from 'react';

// Below this width the sidebar collapses to a slide-in drawer so the
// content pane has room to breathe. 720 is roughly the smallest viewport
// on which the desktop side-by-side layout still feels usable.
export const NARROW_BREAKPOINT_PX = 720;

export function useNarrow(threshold = NARROW_BREAKPOINT_PX): boolean {
  const [narrow, setNarrow] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${threshold - 1}px)`);
    setNarrow(mq.matches);
    const update = () => setNarrow(mq.matches);
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, [threshold]);
  return narrow;
}

/**
 * `useNarrow`, but the matchMedia read happens DURING the first render instead of in a post-paint
 * effect — so the viewport is correct on the component's very first frame, with no desktop-default
 * flash.
 *
 * `useNarrow` initialises to `false` (desktop) and corrects in an effect, which is right for chrome
 * that renders on the server and hydrates: a lazy read there would mismatch. This variant is for a
 * CLIENT-ONLY screen that never appears in the SSR/hydration tree — the Overview home mounts only
 * after the client-side home-variant gate resolves — where reading synchronously is both safe and
 * necessary, because a mobile reader must not first paint a desktop-only treatment and then shift (the
 * Spaces strip appearing, the brief→section gap going 32→24, the brief title changing weight). SSR and
 * a matchMedia-less environment fall back to `false`; the effect still subscribes so a live viewport
 * change is tracked.
 */
export function useViewportNarrow(threshold = NARROW_BREAKPOINT_PX): boolean {
  const query = `(max-width: ${threshold - 1}px)`;
  const [narrow, setNarrow] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false,
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    setNarrow(mq.matches);
    const update = () => setNarrow(mq.matches);
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, [query]);
  return narrow;
}
